import React, { useMemo, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Iconify from "../../../components/iconify";
import BankingAccountsTable from "../settings/Banking/BankingAccountsTable";
import BankingStatistics from "./BankingStatistics";
import NewSepaForm from "../../../components/sepa/NewSepaForm";
import FeeCollectionForm from "./FeeCollectionForm";
import {
  useGetBankingAccountsQuery,
  useUpdateBankingAccountMutation,
} from "../../../features/banking/bankingApiSlice";
import { useCreateBookingMutation } from "../../../features/banking/bookingsApiSlice";
import {
  useBulkMarkPaidMutation,
  useGetMembersQuery,
} from "../../../features/members/membersApiSlice";
import {
  useGetEventsQuery,
  useGraduateAttendeesMutation,
  useDeleteEventMutation,
} from "../../../features/events/eventsApiSlice";
import { useGetContractsQuery } from "../../../features/contracts/contractsApiSlice"; // ensure this exists
import { useBulkResetPaidMutation } from "../../../features/members/membersApiSlice";
import { useGetTenantInformationQuery } from "../../../features/tenants/tenantsApiSlice";
import { useDispatch } from "react-redux";
import ResetPayedTillDialog from "./ResetPayedTillDialog";
import { setDialog } from "../../../features/dialog/dialogSlice";
import RemittanceBuilderDialog from "./components/RemittanceBuilderDialog";
import RemittanceSegmentChip from "./components/RemittanceSegmentChip";
import { getTestingFeeByMemberId } from "../../../utils/testingFeeUtils";
import {
  SAMPLE_REM_CONTEXT,
  parseTemplateToSegments,
  renderRemittanceTemplate,
} from "../../../components/sepa/remittanceTokens";

export default function BankingMenu() {
  const [menuState, setMenuState] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [collectionMode, setCollectionMode] = useState("contribution");
  const [selectedTestingId, setSelectedTestingId] = useState("");
  const dispatch = useDispatch();
  const [openReset, setOpenReset] = useState(false);
  const [bulkResetPaid, { isLoading: resetting }] = useBulkResetPaidMutation();
  const [remittanceDialogOpen, setRemittanceDialogOpen] = useState(false);

  const navigate = useNavigate();

  const [bulkMarkPaid] = useBulkMarkPaidMutation();
  const [updateAccount, { isLoading: savingRemittance }] =
    useUpdateBankingAccountMutation();
  const [createBooking] = useCreateBookingMutation();
  const [graduateAttendees] = useGraduateAttendeesMutation();
  const [deleteEvent] = useDeleteEventMutation();

  const { data: members = [], isLoading: loadingMembers } =
    useGetMembersQuery();
  const { data: contracts = [], isLoading: loadingContracts } =
    useGetContractsQuery();
  const { data: events = [], isLoading: loadingEvents } = useGetEventsQuery();
  const { data: tenantInfo } = useGetTenantInformationQuery();

  const { data: accounts = [], isLoading: isLoadingAccounts } =
    useGetBankingAccountsQuery();

  const handleSelectAccount = (account) => {
    setSelectedAccount(account);
    setMenuState(1);
    setCollectionMode("contribution");
  };

  const selectHauptKonto = () => {
    if (accounts?.length) {
      setSelectedAccount(accounts[0]);
      setMenuState(1);
    }
  };

  const goToSettings = () => navigate("/dashboard/settings/banking");
  const showStatistics = () => setMenuState(2);
  const resetMenu = () => {
    setMenuState(0);
    setSelectedAccount(null);
  };

  const handleSaveRemittanceTemplate = async (templateValue) => {
    if (!selectedAccount) return;
    try {
      const payload = { ...selectedAccount, remittanceInfo: templateValue };
      const result = await updateAccount(payload).unwrap();
      setSelectedAccount((prev) =>
        prev ? { ...prev, remittanceInfo: templateValue } : prev,
      );
      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "Verwendungszweck gespeichert",
          data: {
            successText: result?.message || "Vorlage aktualisiert.",
          },
        }),
      );
      setRemittanceDialogOpen(false);
    } catch (error) {
      console.error("Failed to save remittance template", error);
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: {
            errorText:
              error?.data?.message ||
              "Der Verwendungszweck konnte nicht gespeichert werden.",
          },
        }),
      );
    }
  };

  const handleGraduateTestingAttendees = async () => {
    if (!selectedTestingId) return;
    try {
      await graduateAttendees(selectedTestingId).unwrap();
    } catch (error) {
      console.error("Failed to graduate testing attendees", error);
    }
  };

  const handleDeleteTestingList = async () => {
    if (!selectedTestingId) return;
    try {
      await deleteEvent(selectedTestingId).unwrap();
      setSelectedTestingId("");
    } catch (error) {
      console.error("Failed to delete testing list", error);
    }
  };

  // Map account → creditor (exact keys used by NewSepaForm & SEPA utils)
  const creditor = useMemo(() => {
    if (!selectedAccount) return null;
    return {
      creditorName: selectedAccount?.creditorName || "—",
      creditorIBAN: selectedAccount?.creditorIBAN || "",
      creditorBIC: selectedAccount?.creditorBIC || "",
      creditorId: selectedAccount?.creditorId || "",
      remittanceInfo: selectedAccount?.remittanceInfo || "",
    };
  }, [selectedAccount]);

  const remittancePreview = useMemo(() => {
    if (!selectedAccount?.remittanceInfo) return "";
    return renderRemittanceTemplate(
      selectedAccount.remittanceInfo,
      SAMPLE_REM_CONTEXT,
    ).slice(0, 140);
  }, [selectedAccount?.remittanceInfo]);

  const remittanceSegments = useMemo(
    () => parseTemplateToSegments(selectedAccount?.remittanceInfo || ""),
    [selectedAccount?.remittanceInfo],
  );

  const testingEvents = useMemo(
    () =>
      Array.isArray(events)
        ? events.filter((e) => e?.eventType === "TESTING")
        : [],
    [events],
  );

  const selectedTestingEvent = useMemo(
    () =>
      testingEvents.find((e) => {
        const id = String(e?._id || e?.id || "");
        return id === selectedTestingId;
      }) || null,
    [testingEvents, selectedTestingId],
  );

  const testingAttendeeIds = useMemo(() => {
    const list =
      selectedTestingEvent?.hasAttendees ||
      selectedTestingEvent?.attendees ||
      [];
    return list
      .map((item) => String(item?._id || item?.id || item || ""))
      .filter(Boolean);
  }, [selectedTestingEvent]);

  const testingMembers = useMemo(() => {
    if (!testingAttendeeIds.length) return [];
    const idSet = new Set(testingAttendeeIds);
    return members.filter((m) => idSet.has(String(m?._id || m?.id || "")));
  }, [members, testingAttendeeIds]);

  const testingFeeByMemberId = useMemo(() => {
    const fees = tenantInfo?.settings?.testingSettings?.fees || {};
    return getTestingFeeByMemberId(testingMembers, fees);
  }, [tenantInfo, testingMembers]);

  const testingReferenceText = useMemo(() => {
    if (!selectedTestingEvent) return "";
    const date = selectedTestingEvent?.eventDateBegin
      ? dayjs(selectedTestingEvent.eventDateBegin).format("DD.MM.YYYY")
      : "";
    return ["Prüfungsgebühr", selectedTestingEvent?.eventName, date]
      .filter(Boolean)
      .join(" ")
      .slice(0, 140);
  }, [selectedTestingEvent]);

  useEffect(() => {
    if (collectionMode !== "testing") return;
    if (selectedTestingId) return;
    if (testingEvents.length === 1) {
      const id = String(testingEvents[0]?._id || testingEvents[0]?.id || "");
      setSelectedTestingId(id);
    }
  }, [collectionMode, selectedTestingId, testingEvents]);

  const handleCreateBooking = async (bookingType, summary = {}) => {
    const {
      totalAmount,
      bookMonth,
      collectionDate,
      memberIds = [],
      membersCount: membersCountOverride,
      siteIds = [],
      transactions,
      referenceText,
    } = summary;

    const membersCount =
      Number.isFinite(Number(membersCountOverride)) && membersCountOverride >= 0
        ? Number(membersCountOverride)
        : new Set(memberIds.filter(Boolean)).size;

    if (!collectionDate || !Number.isFinite(Number(totalAmount))) {
      return;
    }

    try {
      await createBooking({
        bookingType,
        bookingDate: collectionDate,
        membersCount,
        totalSum: Number(totalAmount),
        bookMonth: bookingType === "contribution" ? bookMonth : undefined,
        referenceText,
        transactionsCount: transactions,
        siteIds,
      }).unwrap();
    } catch (error) {
      console.error("Failed to create booking:", error);
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Fehler",
          data: {
            errorText:
              error?.data?.message ||
              "Die Buchung konnte nicht gespeichert werden.",
          },
        }),
      );
    }
  };

  const handleContributionExport = (xml, summary) =>
    handleCreateBooking("contribution", summary);

  const handleFeeExport = (xml, summary) => handleCreateBooking("fee", summary);

  const handleTestingExport = (xml, summary) =>
    handleCreateBooking("testing", summary);

  const handleUpdatePayedTill = async (
    memberIds,
    payedTill,
    regMemberIds = [],
  ) => {
    const hasMemberIds = Array.isArray(memberIds) && memberIds.length > 0;
    const hasRegMemberIds =
      Array.isArray(regMemberIds) && regMemberIds.length > 0;

    if (!hasMemberIds && !hasRegMemberIds) return;
    try {
      const res = await bulkMarkPaid({
        memberIds: hasMemberIds ? memberIds : [],
        payedTill,
        regMemberIds: hasRegMemberIds ? regMemberIds : [],
      }).unwrap();
      console.log("Marked members as paid:", res);
    } catch (e) {
      console.error("Failed to mark members as paid:", e);
    }
  };

  switch (menuState) {
    case 0:
      return (
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: 3,
            minHeight: "100%",
            background: (theme) =>
              `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
          }}
        >
          <Stack spacing={3}>
            <Card
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                background: (theme) =>
                  `linear-gradient(120deg, ${theme.palette.primary.light}, ${theme.palette.info.light})`,
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{ color: "primary.contrastText" }}
                    >
                      SEPA-Einzug starten
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: "primary.contrastText", opacity: 0.9 }}
                    >
                      Wählen Sie ein Konto und starten Sie die Lastschrift in
                      wenigen Schritten.
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Stack spacing={0.5}>
                  <Typography variant="h5">
                    Konto für den SEPA-Einzug auswählen
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tipp: Das Standardkonto verwenden, wenn Sie schnell starten
                    möchten.
                  </Typography>
                </Stack>

                <Grid container spacing={2} alignItems="stretch">
                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Typography variant="subtitle1">
                            Schnellstart
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Verwenden Sie das erste Konto in Ihrer Liste.
                          </Typography>
                          <Button
                            variant="contained"
                            color="secondary"
                            disabled={!accounts?.length}
                            onClick={selectHauptKonto}
                            startIcon={<Iconify icon={"mdi:bank"} />}
                          >
                            Standardkonto verwenden
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Typography variant="subtitle1">
                            Kontenübersicht
                          </Typography>
                          <BankingAccountsTable
                            bankingAccounts={accounts}
                            handleSelectedAccount={handleSelectAccount}
                            isLoading={isLoadingAccounts}
                          />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {!accounts?.length && (
                  <Alert
                    severity="info"
                    sx={{ alignItems: "center" }}
                    action={
                      <Button color="primary" onClick={goToSettings}>
                        Einstellungen öffnen
                      </Button>
                    }
                  >
                    Es sind noch keine Konten hinterlegt. Bitte fügen Sie zuerst
                    ein Konto hinzu.
                  </Alert>
                )}
              </Stack>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                  <Stack spacing={2}>
                    <Typography variant="h6">Auswertung</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Behalten Sie Einzüge und Summen im Blick.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={showStatistics}
                      startIcon={<Iconify icon={"mdi:chart-line"} />}
                    >
                      Auswertung öffnen
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2, height: "100%" }}>
                  <Stack spacing={2}>
                    <Typography variant="h6">Zahlstatus</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Setzen Sie den Zahlungsstatus gesammelt zurück.
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => setOpenReset(true)}
                        startIcon={<Iconify icon={"mdi:refresh"} />}
                      >
                        Zahlstatus zurücksetzen
                      </Button>
                      {resetting && (
                        <Typography variant="body2">Zurücksetzen…</Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Stack>

          <ResetPayedTillDialog
            open={openReset}
            onClose={() => setOpenReset(false)}
            onConfirm={async (payload) => {
              try {
                const res = await bulkResetPaid(payload).unwrap();
                setOpenReset(false);
                // Optional: show success via your GlobalDialog
                dispatch(
                  setDialog({
                    type: "SUCCESS",
                    title: "Bezahlt bis zurückgesetzt",
                    data: {
                      successText: `Aktualisiert: ${
                        res.updatedCount
                      }, Übersprungen: ${res.skippedCount ?? 0}`,
                    },
                  }),
                );
              } catch (e) {
                dispatch(
                  setDialog({
                    type: "ERROR",
                    title: "Fehler beim Zurücksetzen",
                    data: {
                      errorText: e?.data?.message || "Aktion fehlgeschlagen.",
                    },
                  }),
                );
              }
            }}
          />
        </Box>
      );

    case 1:
      return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
          <Stack spacing={2}>
            <ReturnButton resetMenu={resetMenu} />
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="h5">SEPA-Einzug erstellen</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Konto: {selectedAccount?.creditorName || "Unbekannt"}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    onClick={() => setMenuState(0)}
                    startIcon={<Iconify icon={"mdi:swap-horizontal"} />}
                  >
                    Konto wechseln
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Paper sx={{ borderRadius: 2 }}>
              <Tabs
                value={collectionMode}
                onChange={(_, value) => value && setCollectionMode(value)}
                sx={{ px: 2 }}
                variant="scrollable"
                allowScrollButtonsMobile
              >
                <Tab label="Mitgliedsbeiträge" value="contribution" />
                <Tab label="Gebühr / Einmalig" value="fee" />
                <Tab label="Prüfungsgebühr" value="testing" />
              </Tabs>
              <Divider />
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {collectionMode === "contribution" && (
                  <>
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography variant="subtitle1">
                          Individueller Verwendungszweck
                        </Typography>
                        {selectedAccount?.remittanceInfo ? (
                          <>
                            <Stack
                              direction="row"
                              spacing={1}
                              flexWrap="wrap"
                              gap={1}
                            >
                              {remittanceSegments.map((segment, index) => (
                                <RemittanceSegmentChip
                                  key={`${segment.type}-${index}`}
                                  segment={segment}
                                />
                              ))}
                            </Stack>
                            {remittancePreview && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Vorschau (Beispieldaten): {remittancePreview}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Noch kein individueller Verwendungszweck hinterlegt.
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            variant="outlined"
                            onClick={() => setRemittanceDialogOpen(true)}
                            startIcon={<Iconify icon={"mdi:pencil"} />}
                          >
                            Verwendungszweck bearbeiten
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                    <NewSepaForm
                      members={members}
                      contracts={contracts}
                      creditor={creditor}
                      onExport={handleContributionExport}
                      onUpdatePayedTill={handleUpdatePayedTill}
                      loading={loadingMembers || loadingContracts}
                    />
                    <RemittanceBuilderDialog
                      open={remittanceDialogOpen}
                      initialTemplate={selectedAccount?.remittanceInfo || ""}
                      onClose={() => setRemittanceDialogOpen(false)}
                      onSave={handleSaveRemittanceTemplate}
                      loading={savingRemittance}
                    />
                  </>
                )}

                {collectionMode === "fee" && (
                  <FeeCollectionForm
                    members={members}
                    creditor={creditor}
                    loading={loadingMembers}
                    onExport={handleFeeExport}
                  />
                )}

                {collectionMode === "testing" && (
                  <>
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 1,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        backgroundColor: (theme) => theme.palette.action.hover,
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography variant="subtitle1">
                          Prüfungsliste auswählen
                        </Typography>
                        {loadingEvents && (
                          <Typography variant="body2">
                            Lade Prüfungen…
                          </Typography>
                        )}
                        {!loadingEvents && testingEvents.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            Keine Prüfungsliste vorhanden.
                          </Typography>
                        )}
                        {testingEvents.length > 0 && (
                          <FormControl sx={{ maxWidth: 520 }}>
                            <InputLabel id="testing-list-label">
                              Prüfungsliste
                            </InputLabel>
                            <Select
                              labelId="testing-list-label"
                              label="Prüfungsliste"
                              value={selectedTestingId}
                              onChange={(e) =>
                                setSelectedTestingId(e.target.value)
                              }
                            >
                              {testingEvents.map((ev) => {
                                const id = String(ev?._id || ev?.id || "");
                                const date = ev?.eventDateBegin
                                  ? dayjs(ev.eventDateBegin).format(
                                      "DD.MM.YYYY",
                                    )
                                  : "ohne Datum";
                                const attendeesCount = Array.isArray(
                                  ev?.hasAttendees,
                                )
                                  ? ev.hasAttendees.length
                                  : 0;
                                return (
                                  <MenuItem key={id} value={id}>
                                    {ev.eventName || "Prüfung"} ({date}) –{" "}
                                    {attendeesCount} Teilnehmer
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        )}
                      </Stack>
                    </Box>

                    {!selectedTestingEvent && testingEvents.length > 1 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Bitte wählen Sie zuerst eine Prüfungsliste aus.
                      </Typography>
                    )}

                    {selectedTestingEvent &&
                      testingAttendeeIds.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Die ausgewählte Prüfungsliste enthält keine
                          Teilnehmer.
                        </Typography>
                      )}

                    {selectedTestingEvent && testingAttendeeIds.length > 0 && (
                      <FeeCollectionForm
                        key={`${selectedTestingId}-${testingAttendeeIds.length}`}
                        members={testingMembers}
                        creditor={creditor}
                        loading={loadingMembers || loadingEvents}
                        title="SEPA-Lastschrift (Prüfungsgebühr)"
                        description="Es werden nur Teilnehmer der ausgewählten Prüfung angezeigt."
                        defaultReferenceText={testingReferenceText}
                        defaultOnlyActive={false}
                        defaultSelectAll
                        defaultFeeByMemberId={testingFeeByMemberId}
                        showTestingActions
                        onGraduateMembers={handleGraduateTestingAttendees}
                        onDeleteTestingList={handleDeleteTestingList}
                        onExport={handleTestingExport}
                      />
                    )}
                  </>
                )}
              </Box>
            </Paper>
          </Stack>
        </Box>
      );

    case 2:
      return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
          <Stack spacing={2}>
            <ReturnButton resetMenu={resetMenu} />
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Auswertung
              </Typography>
              <BankingStatistics />
            </Paper>
          </Stack>
        </Box>
      );

    default:
      return (
        <div>
          <Typography variant="h5">
            Etwas ist schiefgelaufen. Bitte laden Sie die Seite neu.
          </Typography>
        </div>
      );
  }
}

function ReturnButton({ resetMenu }) {
  return (
    <Box>
      <Button
        variant="outlined"
        color="info"
        onClick={resetMenu}
        startIcon={<Iconify icon={"mdi:arrow-left"} />}
      >
        Zurück
      </Button>
    </Box>
  );
}
